import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  InputLabel,
  FormControl,
  MenuItem,
  Select,
  Snackbar,
  Alert,
  TextField,
  Typography,
  CircularProgress,
  Divider,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import CountryFlagLabel from '../../components/CountryFlagLabel';
import SettingsPageHeader from '../../components/SettingsPageHeader';
import {
  SMART_COLORS as COLORS,
  smartCardSx as cardSx,
} from '../../theme/smartStableTheme';

const apiUrl = process.env.REACT_APP_API_URL;

// السعودية كخيار افتراضي ثابت في قائمة بلد المنشأ
const SAUDI_COUNTRY_ID = 149;

// نسخة axios خاصة بالصفحة: baseURL + Authorization تلقائيًا
// (بدون العبث بإعدادات axios العامة)
const api = axios.create({ baseURL: apiUrl });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const SectionCard = ({ title, children }) => (
  <Card elevation={0} sx={{ ...cardSx, mb: 2.5 }}>
    <CardContent sx={{ p: { xs: 2, md: 3 } }}>
      <Typography
        variant="h6"
        sx={{ color: COLORS.text, fontWeight: 900, mb: 0.5 }}
      >
        {title}
      </Typography>
      <Divider sx={{ borderColor: COLORS.line, mb: 2 }} />
      <Grid container spacing={2}>
        {children}
      </Grid>
    </CardContent>
  </Card>
);

const HorseRegistration = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isRtl = String(i18n.language || '').startsWith('ar');

  const [form, setForm] = useState({
    name: '',
    nameArabic: '',
    fatherName: '',
    fatherNameArabic: '',
    motherName: '',
    motherNameArabic: '',
    breederName: '',
    breederNameArabic: '',
    breedId: '',
    rfidChipNumber: '',
    colorId: '',
    gender: '',
    dateOfBirth: '',
    ownerId: '',
    countryOfOriginId: '',
    registrationNumber: '',
    registrationDate: '',
    pedigreeHtml: '',
    allBreedUrl: '',
  });
  const [image, setImage] = useState(null);
  const [registrationCertificate, setRegistrationCertificate] = useState(null);

  const [owners, setOwners] = useState([]);
  const [colors, setColors] = useState([]);
  const [breeds, setBreeds] = useState([]);
  const [countries, setCountries] = useState([]);

  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const setField = (key) => (e) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  // معاينة الصورة قبل الرفع
  const imagePreviewUrl = useMemo(
    () => (image ? URL.createObjectURL(image) : ''),
    [image]
  );
  useEffect(() => {
    return () => {
      if (imagePreviewUrl) URL.revokeObjectURL(imagePreviewUrl);
    };
  }, [imagePreviewUrl]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [ownersRes, colorsRes, breedsRes, countriesRes] =
          await Promise.all([
            api.get('/owners/summary'),
            api.get('/horse_colors'),
            api.get('/horse_breeds'),
            api.get('/countries'),
          ]);
        setOwners(ownersRes.data);
        setColors(colorsRes.data);
        setBreeds(breedsRes.data);
        setCountries(countriesRes.data);
      } catch (err) {
        console.error('Failed to fetch data: ' + err.message);
        setError(t('error_loading_horse_data'));
      }
    };
    fetchData();
  }, [t]);

  // يجمع كل الأخطاء معًا بدل التوقف عند أول خطأ
  const validateFields = () => {
    const errs = {};
    if (!form.name) errs.name = t('please_fill_horse_name');
    if (!form.nameArabic) errs.nameArabic = t('please_fill_horse_name_arabic');
    if (!form.gender) errs.gender = t('please_select_gender');
    if (!form.dateOfBirth) errs.dateOfBirth = t('please_select_date_of_birth');
    if (!registrationCertificate)
      errs.registrationCertificate = t(
        'please_upload_registration_certificate'
      );
    setFieldErrors(errs);
    if (Object.keys(errs).length) {
      setError(t('fix_errors_below'));
      return false;
    }
    setError('');
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateFields()) return;

    setSubmitting(true);
    const formData = new FormData();
    formData.append('name', form.name);
    formData.append('name_arabic', form.nameArabic);
    formData.append('father_name', form.fatherName);
    formData.append('father_name_arabic', form.fatherNameArabic);
    formData.append('mother_name', form.motherName);
    formData.append('mother_name_arabic', form.motherNameArabic);
    formData.append('breeder_name', form.breederName);
    formData.append('breeder_name_arabic', form.breederNameArabic);
    formData.append('breed_id', form.breedId);
    formData.append('rfid_chip_number', form.rfidChipNumber);
    formData.append('color_id', form.colorId);
    formData.append('gender', form.gender);
    formData.append('date_of_birth', form.dateOfBirth);
    formData.append('owner_id', form.ownerId);
    formData.append('country_of_origin_id', form.countryOfOriginId);
    formData.append('registration_number', form.registrationNumber);
    formData.append('registration_date', form.registrationDate);
    formData.append('pedigree_html', form.pedigreeHtml);
    formData.append('allbreed_url', form.allBreedUrl);
    // ملاحظة: created_by يُشتق في الخادم من التوكن (JWT) ولا يُرسل من هنا
    if (image) formData.append('image', image);
    if (registrationCertificate)
      formData.append('registration_certificate', registrationCertificate);

    try {
      await api.post('/horses', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSuccess(t('horse_registered_successfully'));
      setError('');
      // امنح المستخدم لمحة عن رسالة النجاح قبل الانتقال
      setTimeout(() => navigate('/horse-list'), 900);
    } catch (err) {
      const msgKey = err?.response?.data?.message;
      setError(msgKey ? t(msgKey) : t('error_registering_horse'));
      setSuccess('');
      setSubmitting(false);
    }
  };

  const genderOptions = [
    { value: 'S', label: t('horse_stallion') },
    { value: 'M', label: t('mare') },
    { value: 'G', label: t('gelding') },
  ];

  return (
    <Container
      component="main"
      maxWidth="md"
      sx={{ py: 3 }}
      dir={isRtl ? 'rtl' : 'ltr'}
    >
      <SettingsPageHeader
        title={t('register_horse')}
        subtitle={t('horse_singular')}
        backTo="/horse-list"
      />

      <Box component="form" onSubmit={handleSubmit} noValidate>
        {/* القسم 1: البيانات الأساسية */}
        <SectionCard title={t('basic_information')}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              id="name"
              label={t('horse_name')}
              value={form.name}
              onChange={setField('name')}
              error={Boolean(fieldErrors.name)}
              helperText={fieldErrors.name || ''}
              autoFocus
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              id="nameArabic"
              label={t('horse_name_arabic')}
              value={form.nameArabic}
              onChange={setField('nameArabic')}
              error={Boolean(fieldErrors.nameArabic)}
              helperText={fieldErrors.nameArabic || ''}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth required error={Boolean(fieldErrors.gender)}>
              <InputLabel id="gender-label">{t('gender')}</InputLabel>
              <Select
                labelId="gender-label"
                id="gender"
                value={form.gender}
                label={t('gender')}
                onChange={setField('gender')}
              >
                {genderOptions.map((g) => (
                  <MenuItem key={g.value} value={g.value}>
                    {g.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              id="dateOfBirth"
              label={t('date_of_birth')}
              type="date"
              InputLabelProps={{ shrink: true }}
              value={form.dateOfBirth}
              onChange={setField('dateOfBirth')}
              error={Boolean(fieldErrors.dateOfBirth)}
              helperText={fieldErrors.dateOfBirth || ''}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="breedId-label">{t('breed')}</InputLabel>
              <Select
                labelId="breedId-label"
                id="breedId"
                value={form.breedId}
                label={t('breed')}
                onChange={setField('breedId')}
              >
                {breeds.map((breed) => (
                  <MenuItem key={breed.breed_id} value={breed.breed_id}>
                    {breed.breed_english} / {breed.breed_arabic}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="colorId-label">{t('color_id')}</InputLabel>
              <Select
                labelId="colorId-label"
                id="colorId"
                value={form.colorId}
                label={t('color_id')}
                onChange={setField('colorId')}
              >
                {colors.map((color) => (
                  <MenuItem key={color.color_id} value={color.color_id}>
                    {color.color_english} / {color.color_arabic}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              id="rfidChipNumber"
              label={t('rfid_chip_number')}
              value={form.rfidChipNumber}
              onChange={setField('rfidChipNumber')}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="ownerId-label">{t('owner_id')}</InputLabel>
              <Select
                labelId="ownerId-label"
                id="ownerId"
                value={form.ownerId}
                label={t('owner_id')}
                onChange={setField('ownerId')}
              >
                {owners.map((owner) => (
                  <MenuItem key={owner.owner_id} value={owner.owner_id}>
                    {owner.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="countryOfOriginId-label">
                {t('country_of_origin')}
              </InputLabel>
              <Select
                labelId="countryOfOriginId-label"
                id="countryOfOriginId"
                value={form.countryOfOriginId}
                label={t('country_of_origin')}
                onChange={setField('countryOfOriginId')}
              >
                <MenuItem value={SAUDI_COUNTRY_ID}>
                  <CountryFlagLabel
                    country={
                      isRtl ? 'المملكة العربية السعودية' : 'Saudi Arabia'
                    }
                  />
                </MenuItem>
                {countries
                  .filter((c) => c.country_id !== SAUDI_COUNTRY_ID)
                  .map((country) => (
                    <MenuItem
                      key={country.country_id}
                      value={country.country_id}
                    >
                      <CountryFlagLabel
                        country={
                          isRtl
                            ? country.country_arabic
                            : country.country_english
                        }
                      />
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
        </SectionCard>

        {/* القسم 2: النسب */}
        <SectionCard title={t('pedigree_information')}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('father_name')}
              value={form.fatherName}
              onChange={setField('fatherName')}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('father_name_arabic')}
              value={form.fatherNameArabic}
              onChange={setField('fatherNameArabic')}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('mother_name')}
              value={form.motherName}
              onChange={setField('motherName')}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('mother_name_arabic')}
              value={form.motherNameArabic}
              onChange={setField('motherNameArabic')}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('breeder_name')}
              value={form.breederName}
              onChange={setField('breederName')}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('breeder_name_arabic')}
              value={form.breederNameArabic}
              onChange={setField('breederNameArabic')}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('pedigree_html')}
              multiline
              rows={4}
              value={form.pedigreeHtml}
              onChange={setField('pedigreeHtml')}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={
                isRtl
                  ? 'رابط AllBreed Pedigree (اختياري)'
                  : 'AllBreed Pedigree URL (optional)'
              }
              value={form.allBreedUrl}
              onChange={setField('allBreedUrl')}
              placeholder="https://beta.allbreedpedigree.com/horse-slug/pedigree"
            />
          </Grid>
        </SectionCard>

        {/* القسم 3: التسجيل */}
        <SectionCard title={t('registration_information')}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('registration_number')}
              value={form.registrationNumber}
              onChange={setField('registrationNumber')}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('registration_date')}
              type="date"
              InputLabelProps={{ shrink: true }}
              value={form.registrationDate}
              onChange={setField('registrationDate')}
            />
          </Grid>
        </SectionCard>

        {/* القسم 4: المرفقات */}
        <SectionCard title={t('attachments')}>
          {/* صورة الحصان */}
          <Grid item xs={12} md={6}>
            <Typography variant="body2" sx={{ color: COLORS.muted, mb: 1 }}>
              {t('horse_image')}
            </Typography>
            <Button
              component="label"
              variant="outlined"
              startIcon={<CloudUploadIcon />}
              sx={{ borderColor: COLORS.line, color: COLORS.text }}
            >
              {t('upload_horse_image')}
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={(e) => setImage(e.target.files[0] || null)}
              />
            </Button>
            {image && (
              <Box sx={{ mt: 1.5 }}>
                <Box
                  component="img"
                  src={imagePreviewUrl}
                  alt={t('image_preview')}
                  sx={{
                    width: 160,
                    height: 'auto',
                    borderRadius: 1,
                    border: `1px solid ${COLORS.line}`,
                    display: 'block',
                  }}
                />
                <Button
                  size="small"
                  color="error"
                  startIcon={<CloseIcon />}
                  onClick={() => setImage(null)}
                  sx={{ mt: 0.5 }}
                >
                  {t('remove_selected')}
                </Button>
              </Box>
            )}
          </Grid>

          {/* شهادة التسجيل */}
          <Grid item xs={12} md={6}>
            <Typography variant="body2" sx={{ color: COLORS.muted, mb: 1 }}>
              {t('upload_registration_certificate')} *
            </Typography>
            <Button
              component="label"
              variant="outlined"
              startIcon={<PictureAsPdfIcon />}
              color={fieldErrors.registrationCertificate ? 'error' : 'primary'}
              sx={{
                borderColor: fieldErrors.registrationCertificate
                  ? undefined
                  : COLORS.line,
                color: fieldErrors.registrationCertificate
                  ? undefined
                  : COLORS.text,
              }}
            >
              {t('upload_registration_certificate')}
              <input
                type="file"
                hidden
                accept="application/pdf,image/jpeg,image/png,image/webp"
                onChange={(e) =>
                  setRegistrationCertificate(e.target.files[0] || null)
                }
              />
            </Button>
            {registrationCertificate && (
              <Box sx={{ mt: 1.5 }}>
                <Typography
                  variant="body2"
                  sx={{ color: COLORS.green, fontWeight: 700 }}
                >
                  {t('new_certificate_selected')}: {registrationCertificate.name}
                </Typography>
                <Button
                  size="small"
                  color="error"
                  startIcon={<CloseIcon />}
                  onClick={() => setRegistrationCertificate(null)}
                  sx={{ mt: 0.5 }}
                >
                  {t('remove_selected')}
                </Button>
              </Box>
            )}
            {fieldErrors.registrationCertificate && (
              <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                {fieldErrors.registrationCertificate}
              </Typography>
            )}
          </Grid>
        </SectionCard>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2, mb: 4 }}>
          <Button
            type="submit"
            variant="contained"
            disabled={submitting}
            sx={{ flex: 1, bgcolor: COLORS.gold, '&:hover': { bgcolor: COLORS.goldHover } }}
            startIcon={
              submitting ? (
                <CircularProgress size={18} color="inherit" />
              ) : null
            }
          >
            {submitting ? t('saving') : t('register_horse')}
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            disabled={submitting}
            onClick={() => navigate('/horse-list')}
            sx={{ flex: 1 }}
          >
            {t('cancel')}
          </Button>
        </Box>
      </Box>

      <Snackbar
        open={Boolean(success)}
        autoHideDuration={2500}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        onClose={() => setSuccess('')}
      >
        <Alert severity="success" variant="filled" onClose={() => setSuccess('')}>
          {success}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default HorseRegistration;
